import { useState, type FormEvent } from 'react';
import { api, ApiError } from '~/lib/api';
import { cn } from '~/lib/cn';
import type { Account } from '~/lib/accounts';
import { SlideOver } from '~/components/ui/SlideOver';

interface NewAccountPanelProps {
  open: boolean;
  onClose: () => void;
  onCreated: (a: Account) => void;
}

/**
 * Creates a labelled funding account for the tenant. The backend mints
 * a NUBAN at Fincra synchronously — a 201 returns an active account with
 * a real reference, any failure (Fincra down, config missing) surfaces
 * inline here and nothing is persisted.
 *
 * `provider` is fixed to 'providus' (the DB enum label — the actual
 * provisioner today is Fincra). Currency defaults to NGN; the DTO
 * accepts any ISO 4217 three-letter code, so the picker surfaces NGN only
 * until downstream products add another currency.
 */
export function NewAccountPanel({
  open,
  onClose,
  onCreated,
}: NewAccountPanelProps): JSX.Element {
  const [label, setLabel] = useState('');
  const [currency, setCurrency] = useState('NGN');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset(): void {
    setLabel('');
    setCurrency('NGN');
    setError(null);
    setSubmitting(false);
  }

  function closeAndReset(): void {
    reset();
    onClose();
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmed = label.trim();
    if (!trimmed) {
      setError('Give the account a label — "Payroll NGN", "Expenses NGN"…');
      return;
    }
    if (trimmed.length > 80) {
      setError('Keep the label under 80 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api<Account>('/dashboard/accounts', {
        method: 'POST',
        body: { label: trimmed, currency, provider: 'providus' },
      });
      onCreated(res);
      closeAndReset();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Could not create the account. Try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SlideOver
      open={open}
      onClose={closeAndReset}
      title="New funding account"
      description="A tenant-held pool a prefunded program debits. Seven mints a NUBAN against your KYB-approved business — usually under a second."
      width={480}
      footer={
        <div className="flex flex-col gap-2">
          {error && (
            <div
              role="alert"
              className="rounded border border-stop/20 bg-stop-tint px-3 py-2 text-[13.5px] leading-[18px] text-stop"
            >
              {error}
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={closeAndReset}
              className="h-9 rounded-md px-3 text-[14px] font-medium text-ink-secondary hover:bg-surface-margin hover:text-ink-primary"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="new-account-form"
              disabled={submitting}
              className="inline-flex h-9 items-center justify-center rounded-md bg-ink-primary px-4 text-[14px] font-semibold text-ink-on-dark hover:bg-ink-primary/90 disabled:opacity-60"
            >
              {submitting ? 'Creating…' : 'Create account'}
            </button>
          </div>
        </div>
      }
    >
      <form
        id="new-account-form"
        onSubmit={onSubmit}
        className="flex flex-col gap-5"
      >
        <Field
          label="Label"
          hint="How this account shows up in the dashboard. One or two words usually."
        >
          <input
            autoFocus
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Payroll NGN"
            maxLength={80}
            className={cn(INPUT, 'h-9')}
          />
        </Field>

        <Field
          label="Currency"
          hint="Must match the product currency of any program that debits this account."
        >
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className={cn(INPUT, 'h-9 appearance-none pr-8')}
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none' stroke='%23525252' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M1 1l4 4 4-4'/%3E%3C/svg%3E\")",
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 10px center',
            }}
          >
            <option value="NGN">NGN — Nigerian naira</option>
          </select>
        </Field>

      </form>
    </SlideOver>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[13.5px] font-medium text-ink-primary">{label}</span>
      {hint && <span className="text-[12.5px] text-ink-watermark">{hint}</span>}
      {children}
    </label>
  );
}

const INPUT = cn(
  'w-full rounded border border-rule-divider bg-surface-margin px-3 text-[14.5px] text-ink-primary',
  'focus:border-ink-primary focus:bg-surface-slip focus:outline-none focus:ring-2 focus:ring-ink-primary/15',
);
